import {create} from 'zustand';
import {ChatMessageType} from 'constants/chatMessageType';
import {SenderType} from 'constants/senderType';

interface ChatRoom {
    id: number;
    name: string;
    createdAt: string;
    chatCount?: number;
    orderId?: number;
    orderStatus?: string;
    orderStatusDescription?: string;
    watchNames?: string;
}

export interface ChatMessage {
    id: number;
    roomId: string;
    content: string;
    senderType: SenderType;
    messageType?: ChatMessageType;
    buttonList?: Array<{id: number; name: string; checked: boolean; url?: string}>;
    imageList?: string[] | null;
    status?: string;
    createdAt: string;
    currentFlowState?: string;
    orderStatus?: string;
    productType?: string;
    productName?: string;
    repairList?: {name: string; amount: number}[];
    repairDuration?: string;
    name?: string | null;
    address?: string | null;
    detailAddress?: string | null;
    phoneNumber?: string | null;
    deliveryType?: string;
    deliveryRequest?: string | null;
    deliveryAmount?: number;
    repairAmount?: number;
    totalAmount?: number;
}

export interface SimilarCaseMessage {
    message: string;
    imgUrls: string[];
    newRoomId: number | null;
}

interface ChatStore {
    hasShownConsultingAlert: boolean;
    setHasShownConsultingAlert: (flag: boolean) => void;
    isCreateChatRoomRequested: boolean;
    setIsCreateChatRoomRequested: (flag: boolean) => void;
    requestedChatRoomId: string | null;
    requestedRoomId: string | null; // REQUESTED 상태의 방 ID (얼럿용)
    currentChatRoomId: string | null;
    chatRooms: ChatRoom[];
    messages: ChatMessage[];
    setRequestedChatRoomId: (id: string | null) => void;
    setRequestedRoomId: (id: string | null) => void; // REQUESTED 방 ID 설정
    setCurrentChatRoomId: (id: string | null) => void;
    setChatRooms: (rooms: ChatRoom[]) => void;
    setMessages: (messages: ChatMessage[]) => void;
    addMessage: (message: ChatMessage) => void;
    clearMessages: () => void;
    // 유저가 채팅 입력시 AI 답변오기전까지 타이핑 상태 관리
    isAIReplying: boolean;
    setIsAIReplying: (isReplying: boolean) => void;
    isCreatingChatRoom: boolean;
    setIsCreatingChatRoom: (isCreating: boolean) => void;
    isChatRoomConnected: boolean;
    setIsChatRoomConnected: (isConnected: boolean) => void;
    isImageUploadMode: boolean;
    setIsImageUploadMode: (isImageUpload: boolean) => void;
    imageUploadMessageId: number | null;
    setImageUploadMessageId: (messageId: number | null) => void;
    similarCaseMessage: SimilarCaseMessage | null;
    setSimilarCaseMessage: (message: SimilarCaseMessage | null) => void;
    // 이미지 업로드 완료된 채팅방 추적 (이어상담 얼럿용)
    imageUploadCompletedRoomId: string | null;
    setImageUploadCompletedRoomId: (roomId: string | null) => void;
    // 이어상담 얼럿 노출 상태
    shouldShowConsultingAlert: boolean;
    setShouldShowConsultingAlert: (shouldShow: boolean) => void;
    // 결제 완료 처리 상태 (모달 노출 방지용)
    isPaymentCompleted: boolean;
    setIsPaymentCompleted: (isCompleted: boolean) => void;
    // AI 마지막 답변에 버튼이 있는 경우: 입력비활성화 처리
    isInputDisabled: boolean;
    setIsInputDisabled: (isDisabled: boolean) => void;
    // 수리 접수 ID (가견적 → 수리 접수 → 수거 신청 플로우에서 사용)
    repairRequestId: number | null;
    repairRequestChatRoomId: string | null; // repairRequestId가 속한 chatRoomId
    setRepairRequestId: (id: number | null, chatRoomId?: string | null) => void;
    // 세션 비활성화 상태 (약관 미동의 시 입력창 비활성화)
    isSessionDisabled: boolean;
    setIsSessionDisabled: (disabled: boolean) => void;
    // 이미지 업로드 진행 중 상태 (버튼 비활성화용)
    isUploading: boolean;
    setIsUploading: (isUploading: boolean) => void;
    // 로그아웃 시 모든 상태 초기화
    resetChatState: () => void;
}

const defaultState: Omit<
    ChatStore,
    | 'setHasShownConsultingAlert'
    | 'setIsCreateChatRoomRequested'
    | 'setRequestedChatRoomId'
    | 'setRequestedRoomId'
    | 'setCurrentChatRoomId'
    | 'setChatRooms'
    | 'setMessages'
    | 'addMessage'
    | 'clearMessages'
    | 'setIsAIReplying'
    | 'setIsCreatingChatRoom'
    | 'setIsChatRoomConnected'
    | 'setIsImageUploadMode'
    | 'setImageUploadMessageId'
    | 'setSimilarCaseMessage'
    | 'setImageUploadCompletedRoomId'
    | 'setShouldShowConsultingAlert'
    | 'setIsPaymentCompleted'
    | 'setIsInputDisabled'
    | 'setRepairRequestId'
    | 'setIsSessionDisabled'
    | 'setIsUploading'
    | 'resetChatState'
> = {
    hasShownConsultingAlert: false,
    isCreateChatRoomRequested: false,
    requestedChatRoomId: null,
    requestedRoomId: null,
    currentChatRoomId: null,
    chatRooms: [],
    messages: [],
    isAIReplying: false,
    isCreatingChatRoom: false,
    isChatRoomConnected: false,
    isImageUploadMode: false,
    imageUploadMessageId: null,
    similarCaseMessage: null,
    imageUploadCompletedRoomId: null,
    shouldShowConsultingAlert: false,
    isPaymentCompleted: false,
    isInputDisabled: false,
    repairRequestId: null,
    repairRequestChatRoomId: null,
    isSessionDisabled: false,
    isUploading: false,
};

export const useChatStore = create<ChatStore>((set, get) => ({
    ...defaultState,

    setHasShownConsultingAlert: (flag: boolean) => set({hasShownConsultingAlert: flag}),
    setIsCreateChatRoomRequested: (flag: boolean) => set({isCreateChatRoomRequested: flag}),
    setRequestedChatRoomId: (id: string | null) => set({requestedChatRoomId: id}),
    setRequestedRoomId: (id: string | null) => set({requestedRoomId: id}),
    setCurrentChatRoomId: (id: string | null) => set({currentChatRoomId: id}),
    setChatRooms: (rooms: ChatRoom[]) => set({chatRooms: rooms}),
    setMessages: (messages: ChatMessage[]) =>
        set(state => {
            if (state.isCreatingChatRoom && messages.length > 0) {
                return {messages: messages, isCreatingChatRoom: false};
            }
            return {messages: messages};
        }),
    addMessage: (message: ChatMessage) =>
        set(state => {
            // 중복 메시지 검사 (같은 ID면 추가하지 않음)
            if (state.messages.some(m => m.id === message.id)) {
                return {isAIReplying: false};
            }

            const updatedMessages = [...state.messages, message];
            if (state.isCreatingChatRoom && updatedMessages.length > 0) {
                return {messages: updatedMessages, isCreatingChatRoom: false, isAIReplying: false};
            }
            // 메시지가 추가되면 AI 타이핑 상태 해제
            return {messages: updatedMessages, isAIReplying: false};
        }),
    clearMessages: () => {
        set({messages: []});
    },
    setIsAIReplying: (isReplying: boolean) => set({isAIReplying: isReplying}),
    setIsCreatingChatRoom: (isCreating: boolean) => set({isCreatingChatRoom: isCreating}),
    setIsChatRoomConnected: (isConnected: boolean) => set({isChatRoomConnected: isConnected}),
    setIsImageUploadMode: (isImageUpload: boolean) => set({isImageUploadMode: isImageUpload}),
    setImageUploadMessageId: (messageId: number | null) => set({imageUploadMessageId: messageId}),
    setSimilarCaseMessage: (message: SimilarCaseMessage | null) => set({similarCaseMessage: message}),
    setImageUploadCompletedRoomId: (roomId: string | null) => set({imageUploadCompletedRoomId: roomId}),
    setShouldShowConsultingAlert: (shouldShow: boolean) => set({shouldShowConsultingAlert: shouldShow}),
    setIsPaymentCompleted: (isCompleted: boolean) => set({isPaymentCompleted: isCompleted}),
    setIsInputDisabled: (isDisabled: boolean) => set({isInputDisabled: isDisabled}),
    setRepairRequestId: (id: number | null, chatRoomId?: string | null) =>
        set({repairRequestId: id, repairRequestChatRoomId: chatRoomId ?? null}),
    setIsSessionDisabled: (disabled: boolean) => set({isSessionDisabled: disabled}),
    setIsUploading: (isUploading: boolean) => set({isUploading}),

    // 로그아웃용 모든 상태 초기화
    resetChatState: () =>
        set({
            hasShownConsultingAlert: false,
            isCreateChatRoomRequested: false,
            requestedChatRoomId: null,
            requestedRoomId: null,
            currentChatRoomId: null,
            chatRooms: [],
            messages: [],
            isAIReplying: false,
            isCreatingChatRoom: false,
            isChatRoomConnected: false,
            isImageUploadMode: false,
            imageUploadMessageId: null,
            similarCaseMessage: null,
            imageUploadCompletedRoomId: null,
            shouldShowConsultingAlert: false,
            isPaymentCompleted: false,
            isInputDisabled: false,
            repairRequestId: null,
            repairRequestChatRoomId: null,
            isSessionDisabled: false,
            isUploading: false,
        }),
}));
